import { useState, useMemo } from "react";
import { cn } from "@/utils/Functions";
import { ArrowBackIos, Psychology, Equalizer, Chat, Comment, EditNote, PersonAddOutlined, ChevronRight, Check, Preview } from "@mui/icons-material";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@/components/ui/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/Tooltip";
import type { ScreenProps } from "./types";
import { ContractEditView } from "../components/ContractEditView";
import { ContractPreviewView } from "../components/ContractPreviewView";
import { ChatView } from "../components/Chat/ChatView";
import { VariablesView } from "../components/Variables/VariablesView";
import { CommentsAndCollaboratorsView } from "../components/CommentsAndCollaborators/CommentsAndCollaboratorsView";
import { DEFAULT_VARIABLES } from "../components/Variables/variables.config";
import { Dialog, DialogPopup, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/Dialog";
import { ContractSign } from "../components/ContractSign";
import { Input } from "@/components/ui/Input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/Select";
import { Checkbox } from "@/components/ui/Checkbox";
import { Textarea } from "@/components/ui/Textarea";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { PATH_ROOT } from "@/routes/paths";
import { useIsDemo } from "@/hooks/common/useIsDemo";
import { useDemoUsers } from "@/hooks/common/useDemoUsers";
import TextRing3D from "@/components/TextRing3D";

export const PreviewScreen = ({ setSelectedScreen }: ScreenProps) => {
  const [variables, setVariables] = useState<Record<string, string>>(DEFAULT_VARIABLES);
  const [currentView, setCurrentView] = useState<'view' | 'edit'>('view');
  const navigate = useNavigate();
  const isDemo = useIsDemo();
  const setView = (view: 'view' | 'edit') => setCurrentView(view);
  const toggleView = () => setCurrentView(currentView === 'view' ? 'edit' : 'view');
  const [sidebarView, setSidebarView] = useState<'chat' | 'variables' | 'comments'>('chat');
  const [isCollaboratorModalOpen, setIsCollaboratorModalOpen] = useState(false);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [isContractSignModalOpen, setIsContractSignModalOpen] = useState(false);
  const [selectedSigners, setSelectedSigners] = useState<User[]>([]);

  const handleVariablesChange = (newVariables: Record<string, string>) => {
    setVariables(newVariables);
  };

  return (
    <div className="min-h-screen">

      {/* add collaborator modal */}
      <AddCollaboratorModal 
        open={isCollaboratorModalOpen}
        onOpenChange={setIsCollaboratorModalOpen}
      />

      {/* add signature modal */}
      <AddSignatureModal 
        open={isSignatureModalOpen}
        onOpenChange={setIsSignatureModalOpen}
        onContinueToSign={(signers) => {
          setSelectedSigners(signers);
          setIsSignatureModalOpen(false);
          setIsContractSignModalOpen(true);
        }}
      />

      {/* contract sign modal */}
      <ContractSign 
        open={isContractSignModalOpen}
        onOpenChange={setIsContractSignModalOpen}
        signers={selectedSigners}
      />

      <div className="max-w-[1100px] mx-auto">
        <div className="h-8 md:h-32"></div>

        <div className="p-4 grid grid-cols-[auto_1fr] items-center gap-1 md:gap-8 pb-6 border-b border-white/10">
          <button className="w-10 h-10 py-2 pr-2" onClick={() => setSelectedScreen('fill')}>
            <ArrowBackIos className="w-10 h-10 text-neutral-600" />
          </button>

          <div className="bg-gradient-to-r from-[#547BB5] to-[#6E53B8] md:min-h-[64px] border border-white/10 p-3 pl-8 rounded-full flex items-center gap-4">
            <Equalizer className="size-6 text-white/40 rotate-90 flex-shrink-0" />
            <div className="w-full flex space-between items-center gap-1">
              <h2 className="text-lg md:text-xl font-medium flex-1">Music Production Agreement</h2>
              <p className="hidden md:block text-sm md:text-base text-white/60 pr-6">Last saved 2 minutes ago</p>
            </div>
          </div>
        </div>

        {/* toolbar */}
        <div className="sticky top-0 flex flex-col  md:flex-row md:space-between md:items-center gap-4 px-6 py-3 border-b border-white/10 bg-neutral-950">
          <div className=" flex gap-4 flex-shrink-0">
            {/* comments button */}
            <Tooltip>
              <TooltipTrigger render={<Comment className="size-5" />} />
                <button 
                  className={cn("relative w-12 h-12 bg-white/10 grid place-items-center rounded-xl")}
                >
                  <Comment className="size-5" />
                  {/* comments badge */}
                  <div className="grid place-items-center absolute -top-1 -right-1 w-5 h-5 bg-[#7A7041] rounded-full">
                    <span className="text-xs text-white">3</span>
                  </div>
                </button>
              <TooltipContent className="text-xs rounded-lg" side="bottom" sideOffset={8}>
                Comments
              </TooltipContent>
            </Tooltip>

            {/* preview button */}
            <Tooltip>
              <TooltipTrigger render={<Preview className="size-5" />} />
                <button 
                  className={cn("relative w-12 h-12 bg-white/10 grid place-items-center rounded-xl", {
                    'bg-white text-neutral-900': currentView === 'view',
                  })}
                  onClick={() => setView('view')}
                >
                  <Preview className="size-5" />
                </button>
              <TooltipContent className="text-xs rounded-lg" side="bottom" sideOffset={8}>
                Preview Contract
              </TooltipContent>
            </Tooltip>
            
            {/* edit contract button */}
            <Tooltip>
              <TooltipTrigger render={<EditNote className="size-5" />} />
                <button 
                  className={cn("w-12 h-12 bg-white/10 grid place-items-center rounded-xl", {
                    'bg-white text-neutral-900': currentView === 'edit',
                  })}
                  onClick={() => setView('edit')}
                >
                  <EditNote className="size-5" />
                </button>
              <TooltipContent className="text-xs rounded-lg" side="bottom" sideOffset={8}>
                Edit Contract
              </TooltipContent>
            </Tooltip>

            {/* add collaborator button */}
            <Tooltip>
              <TooltipTrigger render={<PersonAddOutlined className="size-5 text-neutral-400" />} />
                <ToolbarButton 
                  icon={<PersonAddOutlined className="size-5 text-neutral-400" />} 
                  label="Add Collaborator" 
                  onClick={() => setIsCollaboratorModalOpen(true)}
                />
              <TooltipContent className="text-xs rounded-lg" side="bottom" sideOffset={8}>
                Add Collaborator
              </TooltipContent>
            </Tooltip>

            {/* divider */}
            <div className="h-12 w-px bg-white/10 align-middle"></div>


            {/* ai button */ }
            <Tooltip>
              <TooltipTrigger render={<TextRing3D className="w-full h-full" />} />
                <button
                  onClick={() => {
                    toast.warning('Demo mode', {
                      description: 'AI Assistant is not available in demo mode'
                    })
                  }}
                  className="w-12 h-12 rounded-xl overflow-hidden bg-black relative"
                  style={{ padding: 0 }}
                >
                  <TextRing3D className="w-full h-full" />
                </button>
              <TooltipContent className="text-xs rounded-lg" side="bottom" sideOffset={8}>
                AI Assistant
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger render={<Psychology className="size-5 text-[#547BB5]" />} />
                <button
                  onClick={() => {
                    navigate(`${isDemo ? PATH_ROOT.demo.analysis : PATH_ROOT.main.analysis}/demo_001`);
                  }}
                className="ai-mode-button bg-black w-11 h-11 grid place-items-center rounded-xl transition-colors">
                  <Psychology className="size-5 text-[#547BB5]" />
                </button>
              <TooltipContent className="text-xs rounded-lg" side="bottom" sideOffset={8}>
                IQ Mode
              </TooltipContent>
            </Tooltip>
          </div>

          <div className="flex-shrink-1 flex items-center gap-2 justify-start md:justify-end md:ml-auto">
            <Button 
              className="w-full md:w-auto" 
              variant="outline"
              onClick={() => setIsSignatureModalOpen(true)}
            >
              Save
            </Button>
            <Button 
              className="w-full md:w-auto" 
              variant="outline"
              onClick={toggleView}
            >
              {currentView === 'view' ? 'Edit' : 'Preview'}
            </Button>
            <Button className="w-full md:w-auto" variant="secondary" onClick={() => {
              toast.warning('Demo mode', {
                description: 'Share is not available in demo mode'
              })
            }}>Share</Button>
            <Button 
              className="w-full md:w-auto" 
              variant="default"
              onClick={() => setIsSignatureModalOpen(true)}
            >
              Sign Contract
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto]">
          {/* main content */}
          <div className="">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={currentView}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
              >
                {currentView === 'edit' ? (
                  <ContractEditView variables={variables} onVariablesChange={handleVariablesChange} />
                ) : (
                  <ContractPreviewView variables={variables} />
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* sidebar */}
          <div className="p-4 w-full md:w-[360px] border-l border-white/10 sticky top-16 h-[560px] overflow-y-auto custom-scrollbar">
            {/* Toggle buttons */}
            <div className="flex gap-1 mb-4 p-1 bg-white/5 rounded-lg">
              <button 
                className={cn("flex-1 px-2 py-2 text-xs font-medium rounded-xl transition-colors", {
                  'bg-white text-black': sidebarView === 'chat',
                  'text-neutral-400 hover:text-white': sidebarView !== 'chat'
                })}
                onClick={() => setSidebarView('chat')}
              >
                <Chat className="size-3 inline mr-1" />
                Chat
              </button>
              <button 
                className={cn("flex-1 px-2 py-2 text-xs font-medium rounded-xl transition-colors", {
                  'bg-white text-black': sidebarView === 'variables',
                  'text-neutral-400 hover:text-white': sidebarView !== 'variables'
                })}
                onClick={() => setSidebarView('variables')}
              >
                <EditNote className="size-3 inline mr-1" />
                Variables
              </button>
              <button 
                className={cn("flex-1 px-2 py-2 text-xs font-medium rounded-xl transition-colors", {
                  'bg-white text-black': sidebarView === 'comments',
                  'text-neutral-400 hover:text-white': sidebarView !== 'comments'
                })}
                onClick={() => setSidebarView('comments')}
              >
                <Comment className="size-3 inline mr-1" />
                Activity
              </button>
            </div>

            {sidebarView === 'chat' ? (
              <ChatView />
            ) : sidebarView === 'variables' ? (
              <VariablesView variables={variables} />
            ) : (
              <CommentsAndCollaboratorsView onAddCollaborator={() => setIsCollaboratorModalOpen(true)} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

type ToolbarButtonProps = {
  icon: React.ReactNode;
  label: string;
} & React.ComponentPropsWithoutRef<"button">

const ToolbarButton = ({ icon, label, ...props }: ToolbarButtonProps) => {
  return (
    <button {...props} aria-label={label} className="w-12 h-12 border border-white/10 grid place-items-center rounded-xl hover:bg-white/10 transition-colors">
      {icon}
    </button>
  )
}


function AddCollaboratorModal({ open, onOpenChange }: { open: boolean, onOpenChange: (open: boolean) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup className="p-6 rounded-3xl w-[calc(100%-3rem)] !md:w-[240px]">
        <DialogHeader>
          <DialogTitle>Add Collaborator</DialogTitle>
        </DialogHeader>
        <DialogDescription className="hidden sr-only">
          Add a collaborator to your contract.
        </DialogDescription>
        <hr className="my-1 border-white/10" />
        <div className="space-y-4">
          <label className="text-base text-neutral-400 mb-3 block" htmlFor="email">Email</label>
          <Input className="h-12" id="email" type="email" placeholder="Enter email address" />

          {/* role */}
          <Select>
            <SelectTrigger className="h-12 bg-white/10 border-none">
              <SelectValue>Select Role</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="client">Client — Can View and Sign</SelectItem>
              <SelectItem value="user">User — Can view and sign</SelectItem>
            </SelectContent>
          </Select>

          {/* details */}
          <div className="bg-black/20 p-4 rounded-xl text-neutral-500">
            <p className="flex items-center gap-2">
              <span className="font-medium">Reviewer:</span>
              Can view the contracts and add comments
            </p>
            <p className="flex items-center gap-2">
              <span className="font-medium">Editor:</span>
              Can make changes to the contract text
            </p>
            <p className="flex items-center gap-2">
              <span className="font-medium">Legal Counsel:</span>
              Full editing and approval rights
            </p>
            <p className="flex items-center gap-2">
              <span className="font-medium">Client:</span>
              Can view, comment and provide signature
            </p>
          </div>

        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button>
            Send Invite
            <ChevronRight />
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  )
}

type User = {
  id: string;
  name: string;
  email: string;
  profilePicture: string;
  signed?: boolean;
  signedAt?: string;
}

function useDemoSignUsers(): User[] {
  const { userDisplayInfo, profilePictures } = useDemoUsers();
  return useMemo(() => {
    if (!userDisplayInfo.demo_user_1) return [];
    return [
      {
        id: 'demo_user_1',
        name: userDisplayInfo.demo_user_1.displayName,
        email: userDisplayInfo.demo_user_1.email,
        profilePicture: profilePictures.demo_user_1,
        signed: true,
        signedAt: "2 hours ago",
      },
      {
        id: 'demo_user_2',
        name: userDisplayInfo.demo_user_2.displayName,
        email: userDisplayInfo.demo_user_2.email,
        profilePicture: profilePictures.demo_user_2,
      },
      {
        id: 'demo_user_3',
        name: userDisplayInfo.demo_user_3.displayName,
        email: userDisplayInfo.demo_user_3.email,
        profilePicture: profilePictures.demo_user_3,
      },
    ];
  }, [userDisplayInfo, profilePictures]);
}

function AddSignatureModal({ 
  open, 
  onOpenChange, 
  onContinueToSign 
}: { 
  open: boolean; 
  onOpenChange: (open: boolean) => void;
  onContinueToSign: (signers: User[]) => void;
}) {
  const USERS = useDemoSignUsers();
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [message, setMessage] = useState('');

  const toggleUser = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSendRequest = () => {
    // Include all users who are signed plus any selected users
    const signers = USERS.filter(u => selectedUsers.includes(u.id) || u.signed);
    onContinueToSign(signers);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup className="p-6 rounded-3xl w-[calc(100%-3rem)] md:w-[640px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Request Signature</DialogTitle>
        </DialogHeader>
        <DialogDescription className="hidden sr-only">
          Request signature for your contract.
        </DialogDescription>
        <hr className="my-1 border-white/10" />
        
        <div className="space-y-4">
          {/* Already Signed */}
          {USERS.filter(u => u.signed).map(user => (
            <div key={user.id} className="bg-white/10 p-6 rounded-2xl">
              <div className="flex items-center gap-4">
                <img 
                  src={user.profilePicture} 
                  alt={user.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div className="flex-1">
                  <p className="text-base font-medium text-white">{user.name}</p>
                  <p className="text-sm text-neutral-400">{user.email}</p>
                </div>
                <div className="flex items-center gap-2 text-green-300 text-sm">
                  <Check className="size-4 text-green-300" />
                  Signed {user.signedAt}
                </div>
              </div>
            </div>
          ))}

          <h3 className="text-xl font-medium text-white">Send Signature Request To</h3>
          <div className="space-y-3">
            {USERS.filter(u => !u.signed).map(user => (
              <div 
                key={user.id} 
                className={cn(
                  "border p-4 rounded-2xl cursor-pointer transition-colors",
                  selectedUsers.includes(user.id) 
                    ? "border-white/30 bg-white/5" 
                    : "border-white/10 hover:border-white/20"
                )}
                onClick={() => toggleUser(user.id)}
              >
                <div className="flex items-center gap-4">
                  <Checkbox 
                    checked={selectedUsers.includes(user.id)}
                    onCheckedChange={() => toggleUser(user.id)}
                  />
                  <img 
                    src={user.profilePicture} 
                    alt={user.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <p className="text-base font-medium text-white">{user.name}</p>
                    <p className="text-sm text-neutral-400">{user.email}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="space-y-2">
            <label className="text-sm text-neutral-400" htmlFor="message">Message (Optional)</label>
            <Textarea 
              id="message"
              placeholder="Add a message for the signers…" 
              className="h-24"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSendRequest}>
            Continue to Sign
            <ChevronRight />
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  )
}
